// Sounds (`sfx.ts`, made in the browser, no files): when and how loud. A button in the status
// bar switches between on, work (quiet, only a few sounds) and off; the choice is kept in the
// browser. Sounds on the map get quieter with the distance to the listener and pan to its side.
import { el } from './dom.js';
import { type SfxName, playSfx } from './sfx.js';

const MODE_KEY = 'webtanks-sound';
const OLD_MUTED_KEY = 'webtanks-muted'; // before work mode: '1' = off
const VOLUME = 0.075; // half of the first 0.15 (Wunsch User 2026-10-13)
/** Work mode: this share of the volume (Wunsch User 2026-10-19). */
const WORK_SHARE = 0.15;
/** Work mode plays only these; new sounds stay out of it unless added. */
const WORK: ReadonlySet<SfxName> = new Set(['go', 'afflicted']);
/** Sounds this far from the listener are silent; this far to the side pan fully. */
const HEARING = 900;
const PAN_SPAN = 600;

type Mode = 'on' | 'work' | 'off';
const MODES: Mode[] = ['on', 'work', 'off'];

let mode: Mode = (() => {
  const saved = localStorage.getItem(MODE_KEY);
  if (saved === 'on' || saved === 'work' || saved === 'off') return saved;
  return localStorage.getItem(OLD_MUTED_KEY) === '1' ? 'off' : 'on';
})();
let lastCountdown = 0;
let listener: [number, number] | undefined;

/** Sound switched off (the controller's rumble too). */
export function soundOff(): boolean {
  return mode === 'off';
}

/** Where the player hears from (the own tank, else the middle of the map); set every frame. */
export function setListener(x: number, y: number): void {
  listener = [x, y];
}

/** Sound `name`, at `x`, `y` on the map if given (else as loud as it gets), `pitch` times as high; as the mode allows. */
export function sound(name: SfxName, x?: number, y?: number, pitch = 1): void {
  if (mode === 'off' || (mode === 'work' && !WORK.has(name))) return;
  let [volume, pan] = [mode === 'work' ? VOLUME * WORK_SHARE : VOLUME, 0];
  if (x !== undefined && y !== undefined && listener) {
    const [dx, dy] = [x - listener[0], y - listener[1]];
    volume *= Math.max(0, 1 - Math.hypot(dx, dy) / HEARING) ** 1.5;
    pan = (dx / PAN_SPAN) * 0.8;
  }
  playSfx(name, volume, pan, pitch);
}

/** Called every frame with the countdown's whole seconds left (0 = running): 3, 2, 1, go. */
export function countdownSound(secs: number): void {
  if (secs !== lastCountdown) {
    // 3, 2, 1 each a whole tone higher (Wunsch User 2026-10-23).
    if (secs >= 1 && secs <= 3) sound('count', undefined, undefined, 2 ** ((2 * (3 - secs)) / 12));
    else if (secs === 0 && lastCountdown === 1) sound('go');
    lastCountdown = secs;
  }
}

/** Status bar button: sound on, work (quiet, only a few sounds), off; a click goes to the next. */
export function soundButton(): HTMLElement {
  const button = el('button', { className: 'sound' });
  const show = () => {
    button.textContent = mode === 'on' ? '🔊' : mode === 'work' ? '🔉' : '🔇';
    const next = MODES[(MODES.indexOf(mode) + 1) % MODES.length];
    const label = { on: 'Sound on', work: 'Work: quiet, only the start signal and hits by negative power-ups', off: 'Sound off' };
    button.title = `${label[mode]} · click: ${label[next].split(':')[0]}`;
  };
  button.onclick = () => {
    mode = MODES[(MODES.indexOf(mode) + 1) % MODES.length];
    localStorage.setItem(MODE_KEY, mode);
    show();
    button.blur(); // keys go on steering the tank
  };
  show();
  return button;
}
